import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { parseStoredValue, removeItem, setItem } from './utils';

const NO_VALUE = null;
type NoValue = typeof NO_VALUE;

// We formerly would invalidate a counter to inform hook subscribers of updates. Explicitly invoke subscriber callbacks going forward.
const listeners = new Set<() => void>();
const notifyListeners = () => listeners.forEach(listener => listener());

const subscribe = (callback: () => void) => {
  listeners.add(callback);
  window.addEventListener('storage', callback);
  return () => {
    listeners.delete(callback);
    window.removeEventListener('storage', callback);
  };
};

const readSerializedValue = (key: string): string | null => {
  try {
    return window.localStorage?.getItem?.(key) ?? NO_VALUE;
  } catch {
    return NO_VALUE;
  }
};

// getSnapshot must return a stable reference unless the raw string changes — otherwise object/array values loop forever. Cache the parsed value per key.
const snapshotCache = new Map<string, { serializedValue: string | null; value: unknown }>();

const getSnapshot = <T>(key: string): T | NoValue => {
  const serializedValue = readSerializedValue(key);
  const cached = snapshotCache.get(key);
  if (cached?.serializedValue === serializedValue) {
    return cached.value as T | NoValue;
  }
  const value = parseStoredValue<T>(serializedValue);
  snapshotCache.set(key, { serializedValue, value });
  return value;
};

const useLocalStorage = <T>(key: string, defaultValue: T | NoValue = NO_VALUE) => {
  // getServerSnapshot returns NO_VALUE so the first client render matches the server and avoids a SSR hydration mismatch (React error 418).
  const stored = useSyncExternalStore(
    subscribe,
    () => getSnapshot<T>(key),
    // This is significant. useLocalStorage will execute server-side during page render.
    // We want to avoid hydration SSR client/server mismatch errors. The localStorage APIs only exist on clients where we pull the real world values.
    // We need the initial getServerSnapshot call here to return the same value as the server render, which is NO_VALUE.
    () => NO_VALUE
  );
  const value = stored || defaultValue;

  const getValue = useCallback(() => value, [value]);

  const setValue = useCallback(
    (newValue: T | NoValue) => {
      if (newValue === NO_VALUE) {
        removeItem(key);
      } else {
        setItem(key, newValue);
      }
      notifyListeners();
    },
    [key]
  );

  return useMemo(() => ({ getValue, setValue }), [getValue, setValue]);
};

export default useLocalStorage;
